import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { useOrg } from '../OrgProvider';
import { navigateTo } from '../router';

export function OrgSwitcher() {
  const { user } = useAuth();
  const { orgs, activeOrg, setActiveOrg, isLoading } = useOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (!user || isLoading) return null;

  // No orgs yet — show a prominent create button
  if (orgs.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigateTo('/org/new')}
        className="inline-flex items-center gap-1.5 border border-accent/40 bg-accent-soft px-2.5 py-1.5 text-xs font-medium text-accent hover:bg-accent/20 transition-colors"
      >
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Create organization
      </button>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 border border-rule bg-bg px-2.5 py-1.5 text-xs font-medium text-link hover:bg-accent-soft transition-colors"
      >
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <span className="max-w-[120px] truncate">{activeOrg?.name ?? 'Select org'}</span>
        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[200px] border border-rule bg-panel py-1 shadow-lg">
          {orgs.map((org) => (
            <button
              key={org.id}
              type="button"
              onClick={() => {
                setActiveOrg(org);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                activeOrg?.id === org.id ? 'bg-accent-soft text-accent' : 'text-link hover:bg-bg'
              }`}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded bg-bg text-xs font-medium text-dim">
                {org.name.charAt(0).toUpperCase()}
              </span>
              <span className="truncate">{org.name}</span>
              {activeOrg?.id === org.id && (
                <svg
                  className="ml-auto h-4 w-4 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </button>
          ))}

          <div className="border-t border-rule-soft mt-1 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigateTo('/org/new');
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-dim hover:bg-bg transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create organization
            </button>
            {activeOrg && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigateTo('/org/settings');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-dim hover:bg-bg transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Org settings
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
